import {
  act,
  cleanup,
  fireEvent,
  screen,
  waitFor,
  within,
} from '@testing-library/react'
import {
  type InspectionArea,
  inspectionAreaSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { expect, it } from 'vitest'

import {
  annotationBounds,
  annotationShape,
  areaCard,
  areaTitleField,
  expectDrawingArmed,
  expectDrawingIdle,
  fieldValues,
  getIntegration,
  historicalArea,
  loadEditorSurface,
  loadMeasuredImage,
  peerArea,
  readDocument,
  renderOwner,
  sourceZone,
} from './NiiInspectionAreas.test-support'
import {
  draw,
  finishAnnotationDrag,
  startAnnotationDrag,
} from './zoneAnalysis/testing/niiEditorTestUtils'

const integration = getIntegration()

it('keeps colliding source/area IDs distinct and routes Show, card selection, redraw and direct edits through the owner', async () => {
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  let surface = loadEditorSurface()
  expect(
    document.querySelector(
      'svg[data-annotation-id="nii-source-same-0"] [data-annotation-shape="true"]'
    )
  ).toBeTruthy()
  expect(
    screen.queryByRole('button', { name: /Resize annotation from/ })
  ).toBeNull()
  expect(readDocument().drawingHost?.documentId).toBe('image-a')
  expect(readDocument().drawingTarget?.documentId).toBe('image-a')

  const savedCard = areaCard('Saved same')
  fireEvent.click(savedCard)
  expect(readDocument().selectedDocumentId).toBe('image-history')
  expect(savedCard.getAttribute('aria-current')).toBe('true')
  loadMeasuredImage('/image-history.png')
  expect(readDocument().drawingTarget?.documentId).toBe('image-history')
  loadEditorSurface()
  fireEvent.change(areaTitleField(savedCard, 'same'), {
    target: { value: 'Saved same edited' },
  })
  const instructions = savedCard.querySelector<HTMLTextAreaElement>(
    'textarea#ut-same-instructions'
  )
  if (!instructions) throw new Error('Expected method instructions field')
  fireEvent.change(instructions, {
    target: { value: 'Scan the edited band' },
  })
  expect(annotationShape('nii-area-same')).toBeTruthy()
  expect(
    document.querySelector('svg[data-annotation-id="nii-source-same-0"]')
  ).toBeNull()
  fireEvent.click(within(savedCard).getByRole('button', { name: 'Show' }))
  expect(readDocument().selectedDocumentId).toBe('image-history')

  fireEvent.click(within(savedCard).getByRole('button', { name: 'Redraw' }))
  expect(readDocument().selectedDocumentId).toBe('image-a')
  expectDrawingArmed()
  loadMeasuredImage('/image-a.png')
  expect(readDocument().drawingTarget?.documentId).toBe('image-a')
  surface = loadEditorSurface()
  expect(
    document.querySelector('[data-region-id="nii-source-same-0"]')
  ).toBeTruthy()
  draw(surface, { x: 70, y: 70 }, { x: 130, y: 120 })
  await waitFor(() => expectDrawingIdle())
  expect(
    document.querySelector(
      'svg[data-annotation-id="nii-area-same"] [data-annotation-shape="true"]'
    )
  ).toBeTruthy()

  const rightEdge = screen.getByRole('button', {
    name: 'Resize annotation from right edge',
  })
  startAnnotationDrag(rightEdge, { x: 130, y: 95 }, { x: 150, y: 95 })
  finishAnnotationDrag({ x: 150, y: 95 })
  const resized = annotationBounds('nii-area-same')
  expect(resized.x_start).toBeCloseTo(240, 8)
  expect(resized.y_start).toBeCloseTo(200, 8)
  expect(resized.x_end).toBeCloseTo(560, 8)
  expect(resized.y_end).toBeCloseTo(400, 8)

  const areaShape = document.querySelector(
    'svg[data-annotation-id="nii-area-same"] [data-annotation-shape="true"]'
  )
  if (!areaShape) throw new Error('Expected the redrawn location outline')
  startAnnotationDrag(areaShape, { x: 110, y: 90 }, { x: 130, y: 100 })
  finishAnnotationDrag({ x: 130, y: 100 })
  const after = annotationBounds('nii-area-same')
  expect(after.x_start).toBeCloseTo(320, 8)
  expect(after.y_start).toBeCloseTo(240, 8)
  expect(after.x_end).toBeCloseTo(640, 8)
  expect(after.y_end).toBeCloseTo(440, 8)
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })
  let saved: InspectionArea[] = []
  await waitFor(() => {
    saved = inspectionAreaSchema
      .array()
      .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
    expect(saved[0]?.drawing_bounds?.x_start).toBe(320)
    expect(saved[0]?.drawing_bounds?.y_start).toBe(240)
    expect(saved[0]?.drawing_bounds?.x_end).toBe(640)
    expect(saved[0]?.drawing_bounds?.y_end).toBeCloseTo(440, 8)
  })
  expect(saved.map((area) => area.id)).toEqual(['same', 'peer'])
  expect(saved[0]).toMatchObject({
    id: 'same',
    sourceZoneId: 'same',
    title: 'Saved same edited',
    zone_reference_image_id: 'image-a',
    methodAssignments: [
      {
        ...historicalArea.methodAssignments[0],
        instructions: 'Scan the edited band',
      },
    ],
  })
  expect(saved[0].drawing_bounds).toEqual(after)
  expect(saved[1]).toEqual(peerArea)
}, 15_000)

it('cancels Add when a same-image peer makes its policy unavailable until explicitly rearmed', async () => {
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  let surface = loadEditorSurface()

  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  expectDrawingArmed()

  const malformedPeer = { ...peerArea, drawing_bounds: undefined }
  integration.fieldValues = fieldValues([historicalArea, malformedPeer])
  act(() => integration.notify?.())
  expect(screen.queryByRole('button', { name: 'Cancel drawing' })).toBeNull()
  expect(
    screen.queryAllByText(/missing or invalid image geometry on this image/)
      .length
  ).toBeGreaterThan(0)
  fireEvent.click(screen.getByRole('button', { name: 'Open history fixture' }))
  expect(readDocument().selectedDocumentId).toBe('image-history')
  fireEvent.click(
    within(areaCard('Saved peer')).getByRole('button', { name: 'Show' })
  )
  expect(readDocument().selectedDocumentId).toBe('image-a')
  expect(screen.queryByRole('button', { name: 'Cancel drawing' })).toBeNull()
  loadMeasuredImage('/image-a.png')
  surface = loadEditorSurface()

  integration.fieldValues = fieldValues()
  act(() => integration.notify?.())
  expect(
    screen.queryAllByText(/missing or invalid image geometry on this image/)
  ).toHaveLength(0)
  expect(screen.queryByRole('button', { name: 'Cancel drawing' })).toBeNull()

  draw(surface, { x: 100, y: 100 }, { x: 140, y: 140 })
  expect(
    inspectionAreaSchema
      .array()
      .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  ).toHaveLength(2)

  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  expectDrawingArmed()
  draw(surface, { x: 100, y: 100 }, { x: 140, y: 140 })
  await waitFor(() => expectDrawingIdle())
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })

  const saved = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved).toHaveLength(3)
  expect(saved.slice(0, 2)).toEqual([historicalArea, peerArea])
  expect(saved[2]).toMatchObject({
    sourceZoneId: sourceZone.id,
    zone_reference_image_id: 'image-a',
    drawing_bounds: {
      x_start: 360,
      y_start: 320,
      x_end: 520,
      y_end: 480,
    },
  })
}, 15_000)

it('blocks planning edits while ticket access is loading', () => {
  integration.loading = true
  renderOwner()

  expect(screen.queryByRole('button', { name: 'Add optional area' })).toBeNull()
  expect(areaTitleField(areaCard('Saved same'), 'same').disabled).toBe(true)
})

it('strips planning geometry extras while retaining scalar historical bounds through save retry', async () => {
  const scalarBounds = {
    x_start: 200,
    y_start: 100,
    x_end: 500,
    y_end: 300,
  }
  integration.fieldValues = {
    ...fieldValues([]),
    inspection_areas: wrapFieldValue([
      {
        ...historicalArea,
        drawing_bounds: {
          ...scalarBounds,
          vendorGeometry: { remove: true },
        },
      },
    ]),
  }
  renderOwner()
  const savedCard = areaCard('Saved same')
  fireEvent.click(savedCard)
  expect(readDocument().selectedDocumentId).toBe('image-history')
  const title = areaTitleField(savedCard, 'same')
  fireEvent.change(title, { target: { value: 'Historical label' } })

  integration.failNextSave = true
  fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  await screen.findByText(
    'Could not save inspection areas. Your changes are still available.'
  )
  expect(areaTitleField(savedCard, 'same').value).toBe('Historical label')

  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })
  const savedAreas = unwrapFieldValue<InspectionArea[]>(
    integration.access.fieldValues.inspection_areas
  )
  expect(savedAreas?.[0]?.drawing_bounds).toEqual(scalarBounds)
  const saved = inspectionAreaSchema.array().parse(savedAreas)
  expect(saved[0]?.title).toBe('Historical label')
  expect(saved[0]).toMatchObject({
    id: 'same',
    sourceZoneId: 'same',
    zone_reference_image_id: 'image-history',
    drawing_bounds: historicalArea.drawing_bounds,
  })
})

it('preserves whole-zone selection while Show uses the locked source mark, then lets the source mark select itself', () => {
  const wholeFirst = inspectionAreaSchema.parse({
    id: 'whole-first',
    sourceZoneId: 'same',
    title: 'Whole first',
    scope: 'zone',
    methodAssignments: [],
  })
  const wholeSecond = inspectionAreaSchema.parse({
    id: 'whole-second',
    sourceZoneId: 'same',
    title: 'Whole second',
    scope: 'zone',
    methodAssignments: [],
  })
  renderOwner({ areas: [historicalArea, peerArea, wholeFirst, wholeSecond] })
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  loadEditorSurface()
  expect(readDocument().drawingTarget?.documentId).toBe('image-a')

  const firstCard = areaCard('Whole first')
  const secondCard = areaCard('Whole second')
  fireEvent.click(firstCard)
  expect(firstCard.getAttribute('aria-current')).toBe('true')
  fireEvent.click(secondCard)
  expect(firstCard.hasAttribute('aria-current')).toBe(false)
  expect(secondCard.getAttribute('aria-current')).toBe('true')
  const wholeZoneCard = secondCard
  fireEvent.click(within(wholeZoneCard).getByRole('button', { name: 'Show' }))
  expect(wholeZoneCard.getAttribute('aria-current')).toBe('true')
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('true')

  const source = document.querySelector(
    'svg[data-annotation-id="nii-source-same-0"] [data-annotation-shape="true"]'
  )
  if (!source) throw new Error('Expected a selectable source mark')
  fireEvent.focus(source)
  expect(wholeZoneCard.hasAttribute('aria-current')).toBe(false)
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('true')
})

it('keeps a selected missing-source area visible with editing and redraw disabled, and honors readonly mode', () => {
  const orphan = inspectionAreaSchema.parse({
    ...historicalArea,
    id: 'orphan',
    sourceZoneId: 'missing-source',
    title: 'Orphan area',
  })
  renderOwner({ areas: [orphan] })
  fireEvent.click(screen.getByRole('button', { name: 'Open history fixture' }))
  loadMeasuredImage('/image-history.png')
  loadEditorSurface()
  const orphanShape = document.querySelector(
    'svg[data-annotation-id="nii-area-orphan"] [data-annotation-shape="true"]'
  )
  if (!orphanShape) throw new Error('Expected the historical orphan mark')
  fireEvent.focus(orphanShape)

  const orphanCard = areaCard('Orphan area')
  expect(
    screen.queryByRole('button', { name: /Resize annotation from/ })
  ).toBeNull()
  expect(
    within(orphanCard).getByRole<HTMLButtonElement>('button', {
      name: 'Redraw',
    }).disabled
  ).toBe(true)
  expect(
    within(orphanCard).getByText(/source zone is unavailable/)
  ).toBeTruthy()

  cleanup()
  integration.fieldValues = fieldValues()
  const readonlyView = renderOwner({ readonly: true })
  expect(screen.queryByRole('button', { name: 'Add optional area' })).toBeNull()
  expect(areaTitleField(areaCard('Saved same'), 'same').disabled).toBe(true)
  readonlyView.unmount()
})
